import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { pollTask } from "../lib/tasks";
import { fmtNumber, fmtDays } from "../lib/format";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { ArrowLeft, ChartLineUp, Scales, Sparkle, Plus, Minus, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRESET_LABELS = {
  baseline: { label: "Baseline (sans changement)", couleur: "#525252" },
  g20_cadre_commun: { label: "G20 Cadre Commun (-30%/-1.5pp/+5ans)", couleur: "#D92D20" },
  moratoire_classique: { label: "Moratoire (allongement +10 ans)", couleur: "#DC6803" },
  decote_brady: { label: "Décote Brady (-50%/-2pp)", couleur: "#7A0F0F" },
  renegociation_douce: { label: "Renégociation douce (-10%/-1pp/+3ans)", couleur: "#205D3B" },
};

export default function Scenarios() {
  const [params, setParams] = useState({
    dette: 1500000000000,
    interets_annuels: 40000000000,
    population: 4500000,
    salaire_mensuel_net: 30000,
    duree_ans: 20,
  });
  const [custom, setCustom] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingArg, setGeneratingArg] = useState(null);
  const [argumentaires, setArgumentaires] = useState([]);

  useEffect(() => { loadArguments(); }, []);

  const loadArguments = async () => {
    try {
      const r = await api.get("/scenarios/argumentaires");
      setArgumentaires(r.data);
    } catch (e) {}
  };

  const simulate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/scenarios/simulate", { ...params, custom });
      setResult(r.data);
      toast.success("Simulation lancée");
    } catch (e) { toast.error("Erreur simulation"); }
    finally { setLoading(false); }
  };

  const generateArgument = async (presetKey, scenarioResult) => {
    setGeneratingArg(presetKey);
    toast.info("Argumentaire IA en cours — patientez ~30 s");
    try {
      const ctx = {
        preset: presetKey,
        preset_label: PRESET_LABELS[presetKey]?.label,
        ...params,
      };
      const r = await api.post("/scenarios/argumentaire", { contexte: ctx, simulation: scenarioResult });
      const final = await pollTask(r.data.task_id, { intervalMs: 2500, timeoutMs: 120000 });
      if (final.status === "error") throw new Error(final.error);
      toast.success("Argumentaire juridique généré");
      loadArguments();
      window.location.href = `/scenarios/argumentaires/${final.result_id}`;
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Erreur IA");
    } finally { setGeneratingArg(null); }
  };

  const addCustom = () => setCustom([...custom, { reduction_principal_pct: 20, reduction_taux_pp: 1, allongement_maturite_ans: 5 }]);
  const removeCustom = (i) => setCustom(custom.filter((_, j) => j !== i));
  const updateCustom = (i, key, val) => {
    const next = [...custom];
    next[i] = { ...next[i], [key]: Number(val) || 0 };
    setCustom(next);
  };

  // Build chart data from result
  const chartData = result ? buildChart(result, custom.length) : [];

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <Link to="/dashboard" className="label-mono inline-flex items-center gap-2 mb-6 hover:text-[var(--signal)]" data-testid="scenarios-back">
        <ArrowLeft size={14} /> Retour au dossier
      </Link>

      <div className="mb-10 fade-up">
        <div className="label-mono">§ 08 · Vita-Scenarios</div>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight flex items-center gap-3">
          <ChartLineUp size={40} className="text-[var(--signal)]" /> Simulation IRH 20 ans
        </h1>
        <p className="mt-3 text-[var(--muted)] max-w-3xl">
          Modélisez l'impact d'une restructuration de la dette sur l'<strong>Indice de Restitution Humaine</strong> (IRH) à 20 ans.
          Argumentaire juridique IA pour fonder vos demandes de renégociation auprès des créanciers, du Cadre Commun G20 ou de la Cour des Comptes.
        </p>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-black/15 border border-black/15 mb-8">
        <div className="lg:col-span-5 bg-[var(--paper)] p-6">
          <div className="label-mono">§ Paramètres baseline</div>
          <h2 className="font-serif text-2xl mt-1">Données macro</h2>
          <div className="mt-4 space-y-3">
            <Field label="Dette nationale totale" testid="sc-dette">
              <input type="number" value={params.dette} onChange={(e) => setParams({...params, dette: Number(e.target.value)})}
                className="w-full border border-black px-3 py-2 bg-transparent" />
            </Field>
            <Field label="Intérêts annuels de la dette" testid="sc-interets">
              <input type="number" value={params.interets_annuels} onChange={(e) => setParams({...params, interets_annuels: Number(e.target.value)})}
                className="w-full border border-black px-3 py-2 bg-transparent" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Population" testid="sc-pop">
                <input type="number" value={params.population} onChange={(e) => setParams({...params, population: Number(e.target.value)})}
                  className="w-full border border-black px-3 py-2 bg-transparent" />
              </Field>
              <Field label="Salaire mensuel net (UM)" testid="sc-sal">
                <input type="number" value={params.salaire_mensuel_net} onChange={(e) => setParams({...params, salaire_mensuel_net: Number(e.target.value)})}
                  className="w-full border border-black px-3 py-2 bg-transparent" />
              </Field>
            </div>
            <Field label="Horizon (années)" testid="sc-duree">
              <input type="number" value={params.duree_ans} onChange={(e) => setParams({...params, duree_ans: Number(e.target.value)})}
                className="w-full border border-black px-3 py-2 bg-transparent" />
            </Field>
          </div>
        </div>

        <div className="lg:col-span-7 bg-[var(--paper)] p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-mono">§ Scénarios personnalisés</div>
              <h2 className="font-serif text-2xl mt-1">5 presets + custom</h2>
            </div>
            <button onClick={addCustom} className="border border-black px-3 py-1.5 label-mono hover:bg-black hover:text-[var(--paper)] flex items-center gap-2" data-testid="sc-add-custom">
              <Plus size={12} /> Custom
            </button>
          </div>
          <p className="mt-2 text-sm text-[var(--muted)]">
            Presets pré-câblés : G20 Cadre Commun, Moratoire, Décote Brady, Renégociation douce. Ajoutez vos propres scénarios ci-dessous.
          </p>

          {custom.length > 0 && (
            <div className="mt-4 space-y-3">
              {custom.map((c, i) => (
                <div key={i} className="border border-black/15 p-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end" data-testid={`sc-custom-${i}`}>
                  <Field label="Réduction principal (%)" compact>
                    <input type="number" value={c.reduction_principal_pct} onChange={(e) => updateCustom(i, "reduction_principal_pct", e.target.value)}
                      className="w-full border border-black/30 px-2 py-1 text-sm bg-transparent" />
                  </Field>
                  <Field label="Réduction taux (pp)" compact>
                    <input type="number" step="0.1" value={c.reduction_taux_pp} onChange={(e) => updateCustom(i, "reduction_taux_pp", e.target.value)}
                      className="w-full border border-black/30 px-2 py-1 text-sm bg-transparent" />
                  </Field>
                  <Field label="Allongement (ans)" compact>
                    <input type="number" value={c.allongement_maturite_ans} onChange={(e) => updateCustom(i, "allongement_maturite_ans", e.target.value)}
                      className="w-full border border-black/30 px-2 py-1 text-sm bg-transparent" />
                  </Field>
                  <button onClick={() => removeCustom(i)} className="border border-black/30 p-2 text-[var(--signal)] hover:border-[var(--signal)]">
                    <Minus size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button onClick={simulate} disabled={loading} data-testid="sc-simulate"
            className="mt-6 w-full bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] py-3 label-mono flex items-center justify-center gap-2 disabled:opacity-50">
            <Sparkle size={14} weight="fill" /> {loading ? "Calcul..." : "Lancer la simulation"} <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <>
          {/* Comparative chart */}
          <div className="border border-black/15 bg-[var(--paper)] p-6 mb-8 fade-up">
            <div className="label-mono">§ Projection IRH cumulée</div>
            <h2 className="font-serif text-2xl mt-1">Jours de vie restitués par citoyen sur {params.duree_ans} ans</h2>
            <div className="mt-6 h-96">
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 30, bottom: 10, left: 10 }}>
                  <CartesianGrid stroke="#E5DFD3" strokeDasharray="2 4" />
                  <XAxis dataKey="annee" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                  <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} label={{ value: "Jours cumulés", angle: -90, position: "insideLeft", style: { fontSize: 10, fontFamily: "IBM Plex Mono", fill: "#525252" } }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #000", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 10 }} />
                  {Object.keys(PRESET_LABELS).map((k) => (
                    <Line key={k} type="monotone" dataKey={k} stroke={PRESET_LABELS[k].couleur} strokeWidth={k === "baseline" ? 2 : 1.5} strokeDasharray={k === "baseline" ? "4 4" : "0"} dot={false} name={PRESET_LABELS[k].label} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Cards per preset */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/15 border border-black/15 mb-8">
            {Object.entries(result.presets).map(([key, sc]) => (
              <ScenarioCard key={key} keyName={key} sc={sc} onArg={() => generateArgument(key, sc)} loading={generatingArg === key} />
            ))}
            {result.custom.map((sc, i) => (
              <ScenarioCard key={`c-${i}`} keyName={`custom-${i}`} customLabel={`Custom #${i+1}`} sc={sc} onArg={() => generateArgument(`custom-${i}`, sc)} loading={generatingArg === `custom-${i}`} />
            ))}
          </div>
        </>
      )}

      {/* List of generated arguments */}
      {argumentaires.length > 0 && (
        <div className="border border-black/15 bg-[var(--paper)] p-6">
          <div className="label-mono">§ Argumentaires générés</div>
          <h2 className="font-serif text-2xl mt-1">Pièces juridiques pour renégociation</h2>
          <ul className="mt-4 divide-y divide-black/10">
            {argumentaires.map((a) => (
              <li key={a.id} className="py-3">
                <Link to={`/scenarios/argumentaires/${a.id}`} className="flex items-center justify-between hover:bg-[var(--paper-2)] -mx-2 px-2 py-1">
                  <div>
                    <div className="font-serif">{a.contexte?.preset_label || "Argumentaire"}</div>
                    <div className="font-mono text-xs text-[var(--muted)]">{new Date(a.cree_le).toLocaleString("fr-FR")}</div>
                  </div>
                  <ArrowRight size={14} className="text-[var(--signal)]" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function buildChart(result, customCount) {
  // Use baseline annees as x axis
  const annees = result.presets.baseline.projections.annees;
  return annees.map((y, i) => {
    const row = { annee: `An ${y}` };
    Object.entries(result.presets).forEach(([k, sc]) => {
      row[k] = Number(sc.projections.economie_jours_cumules[i].toFixed(2));
    });
    return row;
  });
}

function Field({ label, children, compact, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className={`label-mono mb-1 ${compact ? "text-[9px]" : ""}`}>{label}</div>
      {children}
    </label>
  );
}

function ScenarioCard({ keyName, customLabel, sc, onArg, loading }) {
  const meta = PRESET_LABELS[keyName] || { label: customLabel || keyName, couleur: "#0F1115" };
  const irh = sc.irh;
  const isBaseline = keyName === "baseline";

  return (
    <div className="bg-[var(--paper)] p-5 flex flex-col" data-testid={`sc-card-${keyName}`}>
      <div className="flex items-start gap-2">
        <div className="w-3 h-3 mt-1.5 shrink-0" style={{ background: meta.couleur }} />
        <div>
          <div className="label-mono">{keyName}</div>
          <div className="font-serif text-lg leading-tight">{meta.label}</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-px bg-black/10 border border-black/10">
        <Stat label="IRH" value={`${irh.irh_pct.toFixed(1)}%`} accent={!isBaseline && irh.irh_pct > 50} />
        <Stat label="Jours/an sauvés" value={fmtDays(irh.jours_economises_par_an, 2)} />
        <Stat label="Cumul citoyen 20y" value={fmtDays(irh.cumul_jours_par_citoyen_20ans, 1)} />
      </div>

      <div className="mt-4 text-xs text-[var(--muted)] space-y-1 font-mono">
        <div>Taux : {sc.baseline.taux_implicite_pct.toFixed(2)}% → {sc.scenario.taux_apres_pct.toFixed(2)}%</div>
        <div>D5 : {sc.baseline.d5_jours_par_an.toFixed(2)}j/an → {sc.scenario.d5_apres_jours_par_an.toFixed(2)}j/an</div>
        <div>Restitution collective : {fmtNumber(irh.cumul_annees_humaines_collectives, 0)} années humaines</div>
      </div>

      {!isBaseline && (
        <button onClick={onArg} disabled={loading} data-testid={`sc-arg-${keyName}`}
          className="mt-auto pt-4 border-t border-black/10 mt-4 label-mono text-[var(--signal)] hover:underline flex items-center gap-2 disabled:opacity-50">
          <Scales size={14} /> {loading ? "Génération..." : "Argumentaire juridique"}
        </button>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-2 ${accent ? "bg-[var(--signal-light)]" : "bg-[var(--paper)]"}`}>
      <div className="label-mono text-[9px]">{label}</div>
      <div className="font-serif text-lg mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
